import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ParaHojeWidget from "../components/ParaHojeWidget";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Phone, FileText, FileSignature, ClipboardCheck, Handshake, Trophy, ExternalLink } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  LineChart, Line,
} from "recharts";

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function MeuDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isDiretor = user?.role === "diretor" || user?.role === "supervisao";

  const [vendedorId, setVendedorId] = useState(params.get("vendedor_id") || "");
  const [vendedores, setVendedores] = useState([]);
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (isDiretor) {
      api.get("/comercial/vendedores").then((r) => setVendedores(r.data));
    }
  }, [isDiretor]);

  useEffect(() => {
    const carregar = async () => {
      try {
        const qs = { mes, ano };
        if (vendedorId) qs.vendedor_id = vendedorId;
        const { data } = await api.get("/dashboard/vendedor", { params: qs });
        setData(data);
      } catch {
        setData(null);
      }
    };
    carregar();
  }, [vendedorId, mes, ano]);

  const cards = useMemo(() => {
    if (!data) return [];
    return [
      { key: "contatos", label: "Contatos", icon: <Phone size={18} />, real: data.cards.contatos, meta: data.meta.meta_contatos },
      { key: "visitas", label: "Visitas", icon: <Handshake size={18} />, real: data.cards.visitas, meta: data.meta.meta_visitas },
      { key: "propostas", label: "Levantamento de Proposta", icon: <FileText size={18} />, real: data.cards.propostas, meta: data.meta.meta_propostas },
      { key: "analise", label: "Análise", icon: <ClipboardCheck size={18} />, real: data.cards.analise ?? 0, meta: data.meta.meta_analise ?? 0 },
      { key: "contratos", label: "Contrato", icon: <FileSignature size={18} />, real: data.cards.contratos ?? 0, meta: data.meta.meta_contratos ?? 0 },
      { key: "fechamentos", label: "Fechamento", icon: <Trophy size={18} />, real: data.cards.fechamentos, meta: data.meta.meta_fechamentos },
    ];
  }, [data]);

  const dadosGrafico = useMemo(() => {
    if (!data) return [];
    const pct = (r, m) => (m && m > 0 ? Math.round((r / m) * 100) : 0);
    return [
      { etapa: "Contatos", Meta: data.meta.meta_contatos, Realizado: data.cards.contatos, pct: pct(data.cards.contatos, data.meta.meta_contatos) },
      { etapa: "Visitas", Meta: data.meta.meta_visitas, Realizado: data.cards.visitas, pct: pct(data.cards.visitas, data.meta.meta_visitas) },
      { etapa: "Lev. Proposta", Meta: data.meta.meta_propostas, Realizado: data.cards.propostas, pct: pct(data.cards.propostas, data.meta.meta_propostas) },
      { etapa: "Análise", Meta: data.meta.meta_analise ?? 0, Realizado: data.cards.analise ?? 0, pct: pct(data.cards.analise ?? 0, data.meta.meta_analise ?? 0) },
      { etapa: "Contrato", Meta: data.meta.meta_contratos ?? 0, Realizado: data.cards.contratos ?? 0, pct: pct(data.cards.contratos ?? 0, data.meta.meta_contratos ?? 0) },
      { etapa: "Fechamento", Meta: data.meta.meta_fechamentos, Realizado: data.cards.fechamentos, pct: pct(data.cards.fechamentos, data.meta.meta_fechamentos) },
    ];
  }, [data]);

  const evolucao = data?.evolucao_semanal || [];

  const corPct = (p) => (p >= 100 ? "#16a34a" : p >= 70 ? "#F97316" : "#dc2626");

  return (
    <div className="space-y-5" data-testid="meu-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            {isDiretor && vendedorId ? `Dashboard — ${data?.vendedor_nome || ""}` : "Meu Dashboard"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Acompanhe seu desempenho em {MESES[mes - 1]}/{ano}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          {isDiretor && (
            <div className="min-w-[200px]">
              <Label className="text-xs">Vendedor</Label>
              <Select value={vendedorId || "_me"} onValueChange={(v) => { const nv = v === "_me" ? "" : v; setVendedorId(nv); setParams(nv ? { vendedor_id: nv } : {}); }}>
                <SelectTrigger data-testid="dash-vendedor"><SelectValue placeholder="Eu mesmo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_me">Eu mesmo (Diretor)</SelectItem>
                  {vendedores.map((v) => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Mês</Label>
            <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
              <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ano</Label>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                  <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isDiretor && (
            <Button variant="outline" onClick={() => navigate("/app/dashboard-equipe")} data-testid="ver-equipe-btn">
              Painel da equipe →
            </Button>
          )}
        </div>
      </div>

      <ParaHojeWidget />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c) => {
          const pct = c.meta > 0 ? Math.min(100, Math.round((c.real / c.meta) * 100)) : 0;
          return (
            <Card key={c.key} className="p-4" data-testid={`card-${c.key}`}>
              <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs uppercase">{c.label}</span>
                {c.icon}
              </div>
              <div className="text-3xl font-display font-bold mt-1" style={{ color: "#1B3A6B" }}>{c.real}</div>
              <div className="text-xs text-muted-foreground mb-2">Meta: {c.meta}</div>
              <Progress value={pct} className="h-2" />
              <div className="text-xs font-medium mt-1" style={{ color: corPct(pct) }}>{pct}%</div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5">
        <h2 className="text-lg font-bold mb-3" style={{ color: "#1B3A6B" }}>Meta × Realizado</h2>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={dadosGrafico} layout="vertical" margin={{ left: 30, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="etapa" type="category" width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Meta" fill="#94a3b8" />
              <Bar dataKey="Realizado">
                {dadosGrafico.map((d, i) => <Cell key={i} fill={corPct(d.pct)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {data?.meta?.valor_meta > 0 && (
          <div className="mt-3 text-sm text-muted-foreground">
            Receita meta do mês: <strong>{fmtMoney(data.meta.valor_meta)}</strong>
          </div>
        )}
      </Card>

      <Card className="p-5" data-testid="evolucao-semanal-card">
        <h2 className="text-lg font-bold mb-3" style={{ color: "#1B3A6B" }}>Evolução semanal</h2>
        {evolucao.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Sem dados de evolução no período.</div>
        ) : (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={evolucao} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="contatos" stroke="#1B3A6B" strokeWidth={2} name="Contatos novos" />
                <Line type="monotone" dataKey="fechamentos" stroke="#16a34a" strokeWidth={2} name="Fechamentos" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-bold mb-3" style={{ color: "#1B3A6B" }}>Meus leads</h2>
        {(!data || data.leads.length === 0) ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nenhum lead atribuído.</div>
        ) : (
          <div className="space-y-2" data-testid="lista-meus-leads">
            {data.leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between p-3 rounded border hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/app/leads/${l.id}`)} data-testid={`lead-item-${l.id}`}>
                <div>
                  <div className="font-medium">{l.nome}</div>
                  <div className="text-xs text-muted-foreground">{l.empresa} · {l.origem}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">{fmtMoney(l.valor_estimado)}</span>
                  <Badge>{l.status_funil}</Badge>
                  <ExternalLink size={14} className="text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
